/**
 * RemoveMasterFileDialog — admin-only confirmation dialog for removing an
 * unnecessary master file (NOT the same as rejection).
 *
 * Distinct from `RejectMasterFileDialog` because the operations have very
 * different side effects:
 *   • Reject   — flips the file to a rejected state, emails the customer,
 *                blocks the Master Received badge, requires a replacement.
 *   • Remove   — soft-deletes the file (hides it from active lists), does
 *                not change rejection, replacement, supplier, Sony or
 *                customer-facing state. Fully restorable from history.
 *
 * Copy and buttons match the spec on the ticket: title "Remove file?",
 * body "This file will be removed from the active master files. This
 * will not reject the master or change the supplier delivery status.",
 * actions Cancel + Remove file.
 */
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { ordersApi } from '../lib/api';

export function RemoveMasterFileDialog({
    open,
    onOpenChange,
    orderId,
    file,
    onRemoved,
}) {
    const [reason, setReason] = useState('duplicate');
    const [submitting, setSubmitting] = useState(false);

    const close = () => {
        if (submitting) return;
        setReason('duplicate');
        onOpenChange(false);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await ordersApi.softDeleteMasterFile(
                orderId, file.id, (reason || '').trim() || 'duplicate',
            );
            const warnings = data?.manifest_warnings || [];
            if (warnings.length) {
                // Non-blocking warning — the removal has already happened
                // on the server. The admin sees the sidecars that still
                // reference this file so they can decide whether to
                // regenerate the manifest.
                const refs = warnings
                    .map((w) => w.sidecar_filename || w.sidecar_id)
                    .join(', ');
                toast.warning(
                    `File removed. Note: still referenced by manifest(s): ${refs}. Review manifest consistency.`,
                    { duration: 8000 },
                );
            } else {
                toast.success('File removed from active master files.');
            }
            onRemoved?.(data?.file || null, warnings);
            setReason('duplicate');
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to remove file');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-md" data-testid="remove-master-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-slate-800">
                        <Trash2 className="h-5 w-5 text-slate-500" />
                        Remove file?
                    </DialogTitle>
                    <DialogDescription className="text-xs leading-relaxed">
                        This file will be removed from the active master files. This will not reject
                        the master or change the supplier delivery status.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2">
                    <div className="text-xs text-slate-500">File</div>
                    <div
                        className="font-mono text-sm bg-slate-50 border rounded px-2 py-1 truncate"
                        title={file?.original_filename}
                        data-testid="remove-master-filename"
                    >
                        {file?.original_filename}
                    </div>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium" htmlFor="remove-master-reason">
                        Reason (optional)
                    </label>
                    <Input
                        id="remove-master-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. duplicate upload"
                        maxLength={200}
                        disabled={submitting}
                        data-testid="remove-master-reason-input"
                    />
                    <p className="text-[10px] text-slate-500">
                        Recorded in order history. Not shown to the customer.
                    </p>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={close}
                        disabled={submitting}
                        data-testid="remove-master-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        className="bg-slate-800 hover:bg-slate-900 text-white"
                        onClick={submit}
                        disabled={submitting}
                        data-testid="remove-master-confirm"
                    >
                        {submitting ? 'Removing…' : 'Remove file'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default RemoveMasterFileDialog;
